import type { Intensity, PrecipKind, PresentWeather } from './types';

// Synoptic present-weather codes: https://docs.synopticdata.com/services/weather-condition-codes
// Up to three conditions are packed into one number as A·80² + B·80 + C.

type Entry = readonly [kind: PrecipKind | 'thunder', intensity: Intensity, thunder?: boolean];

/**
 * Codes without precipitation: obstructions to vision (haze, fog, blowing snow, …), and 45, missing from
 * the published table but sent by road-weather sensors (UDOT) and decoded by the API as "no precip".
 */
const DRY = new Set([6, 7, 8, 9, 10, 11, 30, 31, 32, 33, 34, 35, 39, 40, 45, 68, 69, 70]);

const CODES: Record<number, Entry> = {
  1: ['rain', 'moderate'],
  13: ['rain', 'light'],
  14: ['rain', 'heavy'],
  16: ['rain', 'moderate'],
  51: ['rain', 'light'],
  52: ['rain', 'heavy'],
  2: ['drizzle', 'moderate'],
  17: ['drizzle', 'light'],
  18: ['drizzle', 'heavy'],
  15: ['freezingRain', 'moderate'],
  49: ['freezingRain', 'light'],
  50: ['freezingRain', 'heavy'],
  19: ['freezingRain', 'light'],
  53: ['freezingRain', 'light'],
  54: ['freezingRain', 'moderate'],
  3: ['snow', 'moderate'],
  20: ['snow', 'light'],
  21: ['snow', 'heavy'],
  22: ['snow', 'moderate'],
  55: ['snow', 'light'],
  56: ['snow', 'heavy'],
  24: ['snow', 'moderate'],
  59: ['snow', 'light'],
  60: ['snow', 'heavy'],
  25: ['snow', 'moderate'],
  61: ['snow', 'light'],
  62: ['snow', 'heavy'],
  67: ['snow', 'moderate'],
  36: ['snow', 'light'],
  64: ['snow', 'light'],
  65: ['snow', 'moderate'],
  37: ['snow', 'light'],
  23: ['icePellets', 'moderate'],
  57: ['icePellets', 'light'],
  58: ['icePellets', 'heavy'],
  63: ['icePellets', 'moderate'],
  75: ['icePellets', 'light'],
  76: ['icePellets', 'heavy'],
  4: ['hail', 'moderate'],
  26: ['hail', 'light'],
  27: ['hail', 'heavy'],
  38: ['hail', 'light'],
  41: ['unknown', 'moderate'],
  5: ['thunder', 'moderate'],
  28: ['thunder', 'light'],
  29: ['thunder', 'heavy'],
  66: ['rain', 'moderate', true],
  77: ['rain', 'light', true],
  78: ['rain', 'heavy', true],
};

const RANK: Record<Intensity, number> = { light: 0, moderate: 1, heavy: 2 };

/** Splits a packed condition code into its (up to three) single codes, 0s dropped. */
export function unpackWeatherCode(code: number): number[] {
  if (code < 0) return [code]; // manual reports (tornado, funnel cloud, …) are never packed
  return [Math.floor(code / 6400), Math.floor((code % 6400) / 80), code % 80].filter((c) => c > 0);
}

/** Precipitation (strongest intensity per kind) and thunder in a packed condition code. */
export function decodeWeatherCode(code: number): PresentWeather {
  const weather: PresentWeather = { precipitation: [], thunder: false, complete: true };
  for (const c of unpackWeatherCode(code)) {
    const entry = CODES[c];
    if (!entry) {
      if (!DRY.has(c)) weather.complete = false;
      continue;
    }
    const [kind, intensity, thunder] = entry;
    if (kind === 'thunder' || thunder) weather.thunder = true;
    if (kind === 'thunder') continue;
    const same = weather.precipitation.find((p) => p.kind === kind);
    if (!same) weather.precipitation.push({ kind, intensity });
    else if (RANK[intensity] > RANK[same.intensity]) same.intensity = intensity;
  }
  return weather;
}
